import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { AuthenticatedLayout } from './components/AuthenticatedLayout/AuthenticatedLayout'
import { ActionsLayout } from './components/ActionsLayout/ActionsLayout'
import { RoleGuard } from './components/RoleGuard/RoleGuard'
import { LoginScreen } from './components/LoginScreen/LoginScreen'
import { TenantGroupsPage } from './pages/TenantGroupsPage/TenantGroupsPage'
import { CreateTenantGroupPage } from './pages/CreateTenantGroupPage/CreateTenantGroupPage'
import { TenantsPage } from './pages/TenantsPage/TenantsPage'
import { CreateTenantPage } from './pages/CreateTenantPage/CreateTenantPage'
import { InventorySystemProfilesPage } from './pages/InventorySystemProfilesPage/InventorySystemProfilesPage'
import { CreateInventorySystemProfilePage } from './pages/CreateInventorySystemProfilePage/CreateInventorySystemProfilePage'
import { ConfigsPage } from './pages/ConfigsPage/ConfigsPage'
import { CreateConfigPage } from './pages/CreateConfigPage/CreateConfigPage'
import { TenantGroupDetailPage } from './pages/TenantGroupDetailPage/TenantGroupDetailPage'
import { TenantDetailPage } from './pages/TenantDetailPage/TenantDetailPage'
import { InventorySystemProfileDetailPage } from './pages/InventorySystemProfileDetailPage/InventorySystemProfileDetailPage'
import { ConfigDetailPage } from './pages/ConfigDetailPage/ConfigDetailPage'
import { ConfigDeletePage } from './pages/ConfigDeletePage/ConfigDeletePage'
import { TenantGroupEditPage } from './pages/TenantGroupEditPage/TenantGroupEditPage'
import { TenantEditPage } from './pages/TenantEditPage/TenantEditPage'
import { InventorySystemProfileEditPage } from './pages/InventorySystemProfileEditPage/InventorySystemProfileEditPage'
import { ConfigEditPage } from './pages/ConfigEditPage/ConfigEditPage'
import { VenuesPage } from './pages/VenuesPage/VenuesPage'
import { UnlinkedVenuesPage } from './pages/UnlinkedVenuesPage/UnlinkedVenuesPage'
import { CreateVenuePage } from './pages/CreateVenuePage/CreateVenuePage'
import { VenueDetailPage } from './pages/VenueDetailPage/VenueDetailPage'
import { VenueEditPage } from './pages/VenueEditPage/VenueEditPage'
import { VenueDeletePage } from './pages/VenueDeletePage/VenueDeletePage'
import { InventorySystemProfileToVenuesPage } from './pages/InventorySystemProfileToVenuesPage/InventorySystemProfileToVenuesPage'
import { CreateInventorySystemProfileToVenuePage } from './pages/CreateInventorySystemProfileToVenuePage/CreateInventorySystemProfileToVenuePage'
import { InventorySystemProfileToVenueDetailPage } from './pages/InventorySystemProfileToVenueDetailPage/InventorySystemProfileToVenueDetailPage'
import { InventorySystemProfileToVenueDeletePage } from './pages/InventorySystemProfileToVenueDeletePage/InventorySystemProfileToVenueDeletePage'
import { RebuildInventoryPage } from './pages/RebuildInventoryPage/RebuildInventoryPage'
import { RebuildConfigCachePage } from './pages/RebuildConfigCachePage/RebuildConfigCachePage'
import { RedrivePerformanceUpdatePage } from './pages/RedrivePerformanceUpdatePage/RedrivePerformanceUpdatePage'
import { RedriveTenantGroupPage } from './pages/RedriveTenantGroupPage/RedriveTenantGroupPage'

export function App() {
  const { isAuthenticated } = useAuth()

  return (
    <BrowserRouter>
      <Routes>
        {isAuthenticated ? (
          <Route
            element={
              <RoleGuard>
                <AuthenticatedLayout />
              </RoleGuard>
            }
          >
            <Route
              path="/tenant-groups/create"
              element={<CreateTenantGroupPage />}
            />
            <Route
              path="/tenant-groups/:id/edit"
              element={<TenantGroupEditPage />}
            />
            <Route
              path="/tenant-groups/:id"
              element={<TenantGroupDetailPage />}
            />
            <Route path="/tenant-groups" element={<TenantGroupsPage />} />
            <Route path="/tenants/create" element={<CreateTenantPage />} />
            <Route path="/tenants/:id/edit" element={<TenantEditPage />} />
            <Route path="/tenants/:id" element={<TenantDetailPage />} />
            <Route path="/tenants" element={<TenantsPage />} />
            <Route
              path="/inventory-system-profiles/create"
              element={<CreateInventorySystemProfilePage />}
            />
            <Route
              path="/inventory-system-profiles/:id/edit"
              element={<InventorySystemProfileEditPage />}
            />
            <Route
              path="/inventory-system-profiles/:id"
              element={<InventorySystemProfileDetailPage />}
            />
            <Route
              path="/inventory-system-profiles"
              element={<InventorySystemProfilesPage />}
            />
            <Route path="/configs/create" element={<CreateConfigPage />} />
            <Route path="/configs/:id/edit" element={<ConfigEditPage />} />
            <Route path="/configs/:id/delete" element={<ConfigDeletePage />} />
            <Route path="/configs/:id" element={<ConfigDetailPage />} />
            <Route path="/configs" element={<ConfigsPage />} />
            <Route path="/venues/create" element={<CreateVenuePage />} />
            <Route path="/venues/:id/edit" element={<VenueEditPage />} />
            <Route path="/venues/:id/delete" element={<VenueDeletePage />} />
            <Route path="/venues/:id" element={<VenueDetailPage />} />
            <Route path="/venues" element={<VenuesPage />} />
            <Route path="/unlinked-venues" element={<UnlinkedVenuesPage />} />
            <Route
              path="/isp-to-venues/create"
              element={<CreateInventorySystemProfileToVenuePage />}
            />
            <Route
              path="/isp-to-venues/:ispId/:venueId/delete"
              element={<InventorySystemProfileToVenueDeletePage />}
            />
            <Route
              path="/isp-to-venues/:ispId/:venueId"
              element={<InventorySystemProfileToVenueDetailPage />}
            />
            <Route
              path="/isp-to-venues"
              element={<InventorySystemProfileToVenuesPage />}
            />
            <Route path="/actions" element={<ActionsLayout />}>
              <Route
                index
                element={<Navigate to="rebuild-inventory" replace />}
              />
              <Route
                path="rebuild-inventory"
                element={<RebuildInventoryPage />}
              />
              <Route
                path="rebuild-config-cache"
                element={<RebuildConfigCachePage />}
              />
              <Route
                path="redrive-performance-update"
                element={<RedrivePerformanceUpdatePage />}
              />
              <Route
                path="redrive-tenant-group"
                element={<RedriveTenantGroupPage />}
              />
            </Route>
            <Route
              path="*"
              element={<Navigate to="/tenant-groups" replace />}
            />
          </Route>
        ) : (
          <Route path="*" element={<LoginScreen />} />
        )}
      </Routes>
    </BrowserRouter>
  )
}
